import React from 'react';
import {
    AvailabilityBadge,
    AvatarLarge,
    Button,
    FavoriteIcon,
    IconClose,
    IconSpinner,
    ListingCard,
    MatchingRateSection,
    NamedLink,
    ReportProfile,
    UserDisplayName,
} from '../../components';
import { IconEyeL } from '../../icons';
import { FormattedMessage, injectIntl } from '../../util/reactIntl';
import { ConditionalWrapper } from '../../util/common';
import { createSlug } from '../../util/urlHelpers';
import InfoProtectionSection from '../../components/TransactionPanel/InfoProtectionSection';
import classNames from 'classnames';
import { compose } from 'redux';
import { useIsMobile } from '../../hooks/useIsMobile';
import config from '../../config';
import css from './AsidePanel.css';
import FavoriteBadge from '../../components/ListingCard/FavoriteBadge';

const { userTypeRider, userTypeHorseowner } = config;

const AsidePanel = ({
    otherParty,
    transaction,
    setAsidePanelVisibility,
    navSectionClassName,
    containerRootClassName,
    currentUser,
    currentListing,
    riderListing,
    riderListingsRequestInProgress,
    matchingMatrixLoading,
    matchingScore,
    matchingViewerListing,
    matchingCurrentPageListing,
    intl,
}) => {
    const [isMobile] = useIsMobile(768);

    const {
        attributes: {
            profile: {
                publicData: { userType },
            },
        },
    } = currentUser;

    const { deleted: isDeleted } = currentListing.attributes || {};
    const isHorseowner = userType === userTypeHorseowner; // buyer
    const isRider = userType === userTypeRider; // customer

    const {
        attributes: {
            profile: {
                isAnonym,
                publicData: { userType: otherPartyType },
            },
        },
    } = otherParty;

    const otherPartyIsRider = otherPartyType === userTypeRider;

    const asideComponentClasses = classNames({
        [css.asidePanel]: true,
        [css.asidePanelRider]: isRider,
        [containerRootClassName]: !!containerRootClassName,
    });

    const navSectionClasses = classNames({
        [navSectionClassName]: !!navSectionClassName,
    });

    return (
        <>
            <nav className={navSectionClasses}>
                <h2>
                    <FormattedMessage id="TransactionPanel.detailsHeading" />
                </h2>
                <Button
                    onClick={() => setAsidePanelVisibility(false)}
                    rootClassName={css.close}
                    title="close"
                    type="button"
                >
                    <span>Schliessen</span>
                    <IconClose rootClassName={css.closeIcon} />
                </Button>
            </nav>
            {isDeleted ? (
                <p className={css.listingDeletedNotification}>
                    Angaben sind nicht zulässig, da dieses Produkt vom Marktplatz gelöscht wurde.
                </p>
            ) : (
                <div className={asideComponentClasses}>
                    {isHorseowner && (
                        <section className={css.horseownerActionTab}>
                            {/*
                             *  If FavoriteIcon is missed, the rider-listing is closed
                             *  in most cases the availability of a rider is notAvailable
                             */}
                            {riderListingsRequestInProgress && <IconSpinner />}
                            {riderListing && (
                                <FavoriteIcon
                                    currentListing={riderListing}
                                    rootClassName={css.favorite}
                                    inWishlistClassName={css.favoriteSelected}
                                />
                            )}

                            {otherPartyIsRider && (
                                <AvailabilityBadge user={otherParty} onLightBackground />
                            )}
                        </section>
                    )}
                    <AvatarLarge
                        className={css.otherPartyAvatarLarge}
                        renderSizes="(max-width: 767px) 96px, 240px"
                        user={otherParty}
                    />
                    <h3 className={css.otherPartyDisplayName}>
                        <UserDisplayName user={otherParty} shouldBeTrimmed={isAnonym} />
                    </h3>
                    {!isAnonym && (
                        <NamedLink
                            className={css.otherPartyProfileLink}
                            name="ProfilePage"
                            params={{
                                id: otherParty.id.uuid,
                                userType: otherPartyType,
                            }}
                        >
                            <IconEyeL />
                            <FormattedMessage id="TransactionPanel.viewOtherPartyProfile" />
                        </NamedLink>
                    )}

                    <MatchingRateSection
                        rootClassName={css.matchingContainer}
                        currentUser={currentUser}
                        user={otherParty}
                        viewerListing={
                            /**
                             * horseownerListing relevant for case when there is listingSubstitutionId
                             * otherwise currentListing is a horse listing
                             */
                            matchingViewerListing
                        }
                        currentPageListing={matchingCurrentPageListing}
                        scoreReqInProgress={matchingMatrixLoading}
                        scoreData={matchingScore}
                        showDateRepresentation={false}
                    />
                    <div className={css.delimiter} />
                    <h3 className={css.requestedAdvertisement}>
                        <FormattedMessage id="TransactionPanel.requestedAdvertisement" />
                    </h3>
                    <div className={css.detailCard}>
                        <ConditionalWrapper
                            condition={!isMobile && currentListing.id}
                            wrapper={children => (
                                <NamedLink
                                    isNotRouterLink
                                    name="ListingPage"
                                    params={{
                                        id: currentListing.id.uuid,
                                        slug: createSlug(currentListing),
                                    }}
                                >
                                    {children}
                                </NamedLink>
                            )}
                        >
                            <ListingCard
                                listing={currentListing}
                                maxParagraphHeight={89}
                                className={css.listingCard}
                                identitySectionClassName={css.identitySection}
                                favoriteComponent={
                                    <FavoriteBadge
                                        currentListing={currentListing}
                                        currentTransaction={transaction}
                                    />
                                }
                            />
                        </ConditionalWrapper>
                    </div>
                    <div className={css.delimiter} />
                    <InfoProtectionSection />
                    <ReportProfile
                        user={otherParty}
                        type="profile"
                        initialValues={{ type: 'profile' }}
                    />
                </div>
            )}
        </>
    );
};

export default compose(injectIntl)(AsidePanel);
